/* components/SiteHeader.ts
Global Site Header

Example
<site-header>
    Site header content here
</site-header>
*/

import { Component, Ref, Prop, SimpleEvent } from '@eastsideco/salvo-ts';
import { ThemeComponent } from '@/theme/ThemeComponent';
import { debounce, isNull } from 'lodash';
import { CartMain } from "../Cart/CartMain";
import { PredictiveSearch } from '../PredictiveSearch/PredictiveSearch';
import { SideCart } from '../SideCart/SideCart';

declare global {
    interface Window {
        // eslint-disable-next-line
        _swat?: any;
    }
}

@Component
export class SiteHeader extends ThemeComponent {
    @Prop() wishlistEnabled!: 'true'|'false';
    @Ref() row!: HTMLDivElement;
    @Ref({required:false}) close!: HTMLButtonElement[];
    @Ref() rowHeight!: HTMLDivElement;
    @Ref() count!: HTMLElement;
    @Ref() menuToggle!: HTMLButtonElement;
    @Ref({required:false}) wishlist!: HTMLElement;
    @Ref({required:false}) searchTrigger!: HTMLButtonElement;
    @Ref({required:false}) searchRow!: HTMLElement;

    private cartMain:CartMain = document.body.querySelector('cart-main') as CartMain;
    private sideCart:SideCart|null = document.body.querySelector('side-cart');
    private _onSearchClose = new SimpleEvent<boolean>();
    public get onSearchClose() {
        return this._onSearchClose.asEvent();
    }

    /**
     * Initialise
     */
    init() {
        this.$subscribe(this.cartMain.onCartItemCountChange, debounce((value) => {
            this.updateCartCount(value);
        }, 300));

        this.cartEvent();
        this.setEventListeners();
        this.handleBodyPadding();

        // Wait for Wishlist to load
        setTimeout(()=> {
            this.updateWishlistCount();
            this.handleWishlistAddToCart();
        }, 1500);

        this.handleKeyBoardControl();
    }

    /**
     * Set event listeners.
     */
    setEventListeners() {
        this.$listen(window, 'resize',
            debounce(() => {
                this.handleBodyPadding();
            }, 300)
        );

        for (const close of this.close) {
            this.$listen(close, 'click', () => {
                close.blur();
                this.handleSearch('close');
            });
        }

        this.$listen(this.menuToggle, 'click', () => {
            this.handleDrawerOpen();
            this.closeRewardsModel();
        });

        if (this.searchTrigger) {
            this.$listen(this.searchTrigger, 'click', () => {
                this.handleSearch('open');
                this.closeRewardsModel();
            });
        }

        document.addEventListener('wishlistUpdated', (event) => {
            const detail = (event as CustomEvent).detail;
            this.updateWishlistCount(detail && typeof detail.count === 'number' ? detail.count : undefined);
        });

        if (!this.searchRow) { return; }
        const predictiveSearch:PredictiveSearch|null = this.searchRow. querySelector('predictive-search');
        if (predictiveSearch) {
            this.$subscribe(predictiveSearch.onSearchInput, () => this.handleSearch('open'));
        }
    }

    /**
     * Handle Body Padding.
     */
    private handleBodyPadding() {
        const heightRow = this.rowHeight.offsetHeight;
        document.body.style.paddingTop = `${heightRow}px`;
    }

    /**
     * Cart Icon click event.
     * @returns
     */
    cartEvent() {
        const cartTrigger: HTMLElement|null = this.querySelector('[data-ref="cartTrigger"]');
        if (!cartTrigger) { return; }

        this.$listen(cartTrigger, 'click', (event) => {
            event.preventDefault();
            this.handleSearch('close');

            if (!this.sideCart) { return; }
            this.sideCart.openCart();
        });
    }

    /**
     * Update cart count
     * @returns
     */
    private updateCartCount(count: number) {
        if(!this.count) { return; }

        this.count.innerHTML = String(count);
        if (count > 0) {
            this.count.parentElement?.classList.add('is-active');
        } else {
            this.count.parentElement?.classList.remove('is-active');
        }
    }

    /**
     *
     * @returns Update wishlist count.
     */
    updateWishlistCount(count?: number) {
        if (this.wishlistEnabled == 'false') { return; } // wishlist disabled

        /* eslint-disable */
        const $this = this;
        const render = (value: number) => {
            if (!$this.wishlist) { return; }
            if (value <= 0) {
                $this.wishlist.textContent = '';
                $this.wishlist.parentElement?.classList.remove('is-active');
                return;
            }
            $this.wishlist.textContent = String(value);
            $this.wishlist.parentElement?.classList.add('is-active');
        };

        // Fresh count supplied by the wishlist snippets — render it directly
        // (avoids the stale value _swat.wishlistCount returns right after a change).
        if (typeof count === 'number') { render(count); return; }

        // Otherwise (e.g. initial page load) ask Swym for the count.
        // swym wishlist specific call format fails ts linting.
        if (!window._swat) { return; }
        window._swat.wishlistCount((c:string) => render(~~c));
        /* eslint-enable */
    }

    /**
     * Trigger search bar.
     * @param action - 'open' / 'close'
     * @returns
     */
    handleSearch(action:string) {
        if (!this.searchRow) { return; }
        const search: HTMLInputElement|null = this.searchRow.querySelector('input[type=search]');

        if (action === 'open') {
            this.searchRow.classList.add('is-active');
            document.body.classList.add('drawer-open');
            search?.focus();
            return;
        }

        this.searchRow.classList.remove('is-active');
        document.body.classList.remove('drawer-open');
        this._onSearchClose.dispatch(true);

        if(!search) { return; }
        search.value = '';
    }

    /**
     * Trigger menu drawer.
     * @returns
     */
    handleDrawerOpen() {
        if (window.innerWidth > 1440) { return; }
        const menu = document.querySelector('.mobile-menu');
        const overlay = document.querySelector('.mobile-menu__overlay');

        this.handleSearch('close');
        document.documentElement.classList.add('drawer-open');
        document.body.classList.add('drawer-open');
        menu?.classList.add('is-active');
        overlay?.classList.add('is-active');
    }

    handleWishlistAddToCart() {
        if (this.wishlistEnabled == 'false') { return; } // wishlist disabled

        /* eslint-disable */
        const listener = (event: any) => {
            const data =  event.detail.d.productData;
            console.log(event)
            event.target.style.background = 'red'
            console.log("YAY! Added the product to cart", data);

            setTimeout(()=> {
                this.cartMain.updateCart('all');
            }, 500);
        };

        if (!window._swat) { return; }
        window._swat.evtLayer.addEventListener('sw:addedtocart', listener);
        /* eslint-enable */

    }

    /**
     * Handle Keyboard controls for submenu.
     */

    handleKeyBoardControl(): void {
        let currentlyOpenLink: HTMLElement | null = null;

        const links = document.querySelectorAll<HTMLElement>('.site-header__parent-link');

        for (const link of links) {
            const submenuId = link.getAttribute('aria-controls');
            if (!submenuId) {continue;}

            const submenu = document.getElementById(submenuId);
            if (!submenu) {continue;}

            // Make sure submenu can be interacted with via keyboard
            submenu.setAttribute('tabindex', '-1'); // if needed

            // Attach Escape key handler directly on the submenu
            submenu.addEventListener('keydown', (ev: KeyboardEvent) => {
                if (ev.key === 'Escape') {
                    link.setAttribute('aria-expanded', 'false');
                    submenu.hidden = true;
                    link.focus();
                    currentlyOpenLink = null;
                }
            });

            // Focus event to close previously open submenu
            link.addEventListener('focus', () => {
                if (currentlyOpenLink && currentlyOpenLink !== link) {
                    const prevSubmenuId = currentlyOpenLink.getAttribute('aria-controls');
                    const prevSubmenu = prevSubmenuId ? document.getElementById(prevSubmenuId) : null;
                    if (prevSubmenu) {
                        currentlyOpenLink.setAttribute('aria-expanded', 'false');
                        prevSubmenu.hidden = true;
                    }
                    currentlyOpenLink = null;
                }
            });

            // Handle toggle and Escape on link
            link.addEventListener('keydown', (e: KeyboardEvent) => {
                if (!submenu) {return;}

                if ((e.key === 'Enter' || e.key === ' ') && submenu) {
                    e.preventDefault();

                    const isExpanded = link.getAttribute('aria-expanded') === 'true';

                    // Close previously open submenu
                    if (currentlyOpenLink && currentlyOpenLink !== link) {
                        const prevSubmenuId = currentlyOpenLink.getAttribute('aria-controls');
                        const prevSubmenu = prevSubmenuId ? document.getElementById(prevSubmenuId) : null;
                        if (prevSubmenu) {
                            currentlyOpenLink.setAttribute('aria-expanded', 'false');
                            prevSubmenu.hidden = true;
                        }
                    }

                    // Toggle submenu
                    link.setAttribute('aria-expanded', String(!isExpanded));
                    submenu.hidden = isExpanded;

                    if (!isExpanded) {
                        const firstItem = submenu.querySelector<HTMLElement>('[role="menuitem"]');
                        firstItem?.focus();
                        currentlyOpenLink = link;
                    } else {
                        currentlyOpenLink = null;
                    }
                }

                if (e.key === 'Escape' && link.getAttribute('aria-expanded') === 'true') {
                    link.setAttribute('aria-expanded', 'false');
                    submenu.hidden = true;
                    link.focus();
                    currentlyOpenLink = null;
                }
            });
        }
    }

    // close rewards model
    private closeRewardsModel() {
        const rewards_modal = document.querySelector('.rewards-model');
        if(rewards_modal) {
            rewards_modal.classList.remove('is-visible');
        }
    }

}
